import React, { FC, MouseEvent } from 'react'
import styled from 'styled-components'

import * as PT from 'modules/Pricing/types'

import Spacer from 'components/Spacer'

import * as T from 'types'
import * as E from 'types/enums'

import GridItemContainer from './GridItemContainer'
import PackageCardPricingButton from './PackageCardPricingButton'
import PriceBox from './PriceBox'

interface ITitlePropsArg extends PT.IPricingPackageCardModeProps {
  theme: any
}
const getTextColor = (props: ITitlePropsArg, defaultColor: string) => {
  if (props.mode === E.PackageCardMode.ComingSoon) return props.theme.colors.grey34

  if (props.mode === E.PackageCardMode.Best) return props.theme.colors.white

  return defaultColor
}

const Title = styled.div<PT.IPricingPackageCardModeProps>`
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${props => getTextColor(props, props.theme.colors.neutral800)};

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-weight: 900;
    font-size: 24px;
    line-height: 33px;
  }
`

const Subtitle = styled.div<PT.IPricingPackageCardModeProps>`
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: ${props => getTextColor(props, props.theme.colors.neutral400)};

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 14px;
    line-height: 20px;
  }
`

const Tag = styled.div<PT.IPricingPackageCardModeProps>`
  border-radius: 12px;
  padding: 6px 16px;
  color: ${props => props.theme.colors.teal500};
  background-color: ${props => props.theme.colors.blue1000};
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    position: absolute;
    right: 18px;
    top: 20px;
  }
`

interface IProps extends Partial<PT.IPricingPackageCardModeProps> {
  columnStart: number
  columnEnd: number
  pricingPackage: T.IPricingPackage | undefined
  subtitle: string
  purchasedPackage: E.PackageType | undefined
  packageType: E.PackageType
  selectable: boolean | undefined
  selectedPackage: E.PackageType | null
  onClickSelect(type: E.PackageType): void
  purchased?: boolean
  initialized?: boolean
  disabled?: boolean
}

const PackageCardGridItem: FC<IProps> = ({
  columnStart,
  columnEnd,
  mode,
  subtitle,
  initialized,
  packageType,
  pricingPackage,
  selectable,
  selectedPackage,
  purchased,
  purchasedPackage,
  disabled,
  onClickSelect,
}) => {
  if (!pricingPackage) return null

  const handleSelect = (event: MouseEvent) => {
    event.stopPropagation()
    onClickSelect(packageType)
  }

  const selected = selectedPackage === packageType

  return (
    <>
      <GridItemContainer
        mode={mode}
        rowStart={mode === E.PackageCardMode.Best ? 1 : 3}
        rowEnd={mode === E.PackageCardMode.Best ? 2 : 4}
        columnStart={columnStart}
        columnEnd={columnEnd}
        packageType={packageType}
      >
        {mode === E.PackageCardMode.Best && (
          <>
            <Tag mode={E.PackageCardMode.Best}>BEST VALUE</Tag>
            <Spacer
              size={28}
              hideMobile
              hideTablet
            />
          </>
        )}
        <Spacer
          size={2}
          hideMobile
          hideTablet
        />
        <Title mode={mode}>{pricingPackage?.name}</Title>
      </GridItemContainer>
      <GridItemContainer
        mode={mode}
        rowStart={mode === E.PackageCardMode.Best ? 3 : 5}
        rowEnd={mode === E.PackageCardMode.Best ? 4 : 6}
        columnStart={columnStart}
        columnEnd={columnEnd}
        packageType={packageType}
      >
        <Subtitle mode={mode}>{subtitle}</Subtitle>
      </GridItemContainer>
      <GridItemContainer
        mode={mode}
        rowStart={mode === E.PackageCardMode.Best ? 5 : 7}
        rowEnd={mode === E.PackageCardMode.Best ? 6 : 8}
        columnStart={columnStart}
        columnEnd={columnEnd}
        packageType={packageType}
      >
        <PriceBox
          mode={mode}
          pricingPackage={pricingPackage}
        />
      </GridItemContainer>
      <GridItemContainer
        mode={mode}
        rowStart={mode === E.PackageCardMode.Best ? 7 : 9}
        rowEnd={mode === E.PackageCardMode.Best ? 8 : 10}
        columnStart={columnStart}
        columnEnd={columnEnd}
        packageType={packageType}
      >
        <PackageCardPricingButton
          mode={mode}
          pricingPackage={pricingPackage}
          onClickSelect={selectable ? handleSelect : undefined}
          selected={selected}
          purchased={purchased}
          purchasedPackage={purchasedPackage}
          disabled={disabled}
          initialized={initialized}
        />
      </GridItemContainer>
    </>
  )
}

export default PackageCardGridItem
